import {
  ArrayStepConfig,
  FormElementConfig,
  Option,
} from "@platform/design-system";
import { isBefore, parseISO, startOfDay, subMonths } from "date-fns";

import {
  Carrier,
  ClientProductCarrierWithCarrier,
  Coverage,
  RfpCoveragesResponse,
} from "../../../../types/rfpApi";

import {
  ANNUAL_WRITTEN_PREMIUM_FIELD,
  FINAL_DECISION_STATUS_LABELS,
  FORM_FIELDS,
  MONEY_FIELD,
  NO,
  NOTES_FIELD,
  NUMBER_OF_PLANS_QUOTED_FIELD,
  PERCENTAGE_FIELD,
  RATE_GUARANTEE_FIELD,
  RENEWAL_DATE_FIELD,
  SELECT_OPTION_COVERAGE_FIELD,
  TECH_CREDIT_FIELD,
  WINNING_CARRIER_FIELD,
  WINNING_CONTRACTED_CARRIER_FIELD,
  WINNING_GA_ANNUAL_OVERRIDE_AMOUNT_FIELD,
  WINNING_GA_OVERRIDE_PERCENTAGE_FIELD,
  YES,
} from "./constants";
import {
  CoverageFormValues,
  FinalDecisionFormGeneratorProps,
  FinalDecisionFormValues,
  FinalDecisionStatus,
  InputType,
} from "./types";
import {
  calculateNextRenewalDate,
  getArrayStepperField,
  getParentField,
  getSectionField,
  isBrokerOfRecord,
  isVisibleForFinalDecision,
  isVisibleForIncumbentData,
  isVisibleForSold,
} from "./utils";

export const clearCoverageValues = (
  coverages: CoverageFormValues[],
): CoverageFormValues[] =>
  coverages.map(
    (coverage): CoverageFormValues => ({
      ...coverage,
      finalDecisionSelected: null,
      incumbentProductId: null,
      incumbentCarrierId: null,
      incumbentData: null,
      incumbentAnnualPremium: null,
      incumbentContractedCarrier: null,
      incumbentGaOverridePercentage: null,
      incumbentGaAnnualOverrideAmount: null,
      winningCarrierId: null,
      winningContractedCarrier: null,
      winningMedicalCarrier: null,
      annualWrittenPremium: null,
      rateGuarantee: null,
      renewalDate: null,
      renewalDateManualEntry: null,
      lastEffectiveDate: null,
      incumbentProductIdManualEntry: null,
      techCredits: null,
      winningGaOverridePercentage: null,
      winningGaAnnualOverrideAmount: null,
      sold: false,
      numberOfPlansQuoted: null,
    }),
  );

export const clearCoverageValuesKeepIncumbent = (
  coverages: CoverageFormValues[],
): CoverageFormValues[] =>
  coverages.map(
    (coverage): CoverageFormValues => ({
      ...coverage,
      finalDecisionSelected: null,
      winningCarrierId: null,
      winningContractedCarrier: null,
      winningMedicalCarrier: null,
      annualWrittenPremium: null,
      rateGuarantee: null,
      renewalDate: null,
      renewalDateManualEntry: null,
      lastEffectiveDate: null,
      incumbentProductIdManualEntry: null,
      techCredits: null,
      winningGaOverridePercentage: null,
      winningGaAnnualOverrideAmount: null,
      sold: false,
      numberOfPlansQuoted: null,
    }),
  );

export const cleanCoverages = (
  formMethods: FinalDecisionFormGeneratorProps["formMethods"],
  keepIncumbentData = false,
): void => {
  const currentCoveragesValues =
    (formMethods.getValues("currentCoverages") as CoverageFormValues[]) || [];
  const newCoveragesValues =
    (formMethods.getValues("newCoverages") as CoverageFormValues[]) || [];

  if (currentCoveragesValues.length > 0) {
    const currentCoverages = keepIncumbentData
      ? clearCoverageValuesKeepIncumbent(currentCoveragesValues)
      : clearCoverageValues(currentCoveragesValues);
    formMethods.setValue("currentCoverages", currentCoverages);
  }
  if (newCoveragesValues.length > 0) {
    const newCoverages = clearCoverageValues(newCoveragesValues);
    formMethods.setValue("newCoverages", newCoverages);
  }
};

const updateRenewalDate = (
  coverageValues: CoverageFormValues,
  rateGuaranteeValue: string | undefined,
  effectiveDateToUse: string | null | undefined,
): void => {
  const wasManuallyEdited =
    coverageValues[FORM_FIELDS.RENEWAL_DATE_MANUAL_ENTRY];
  const lastEffectiveDate = coverageValues[FORM_FIELDS.LAST_EFFECTIVE_DATE];
  const lastRateGuarantee = coverageValues[FORM_FIELDS.LAST_RATE_GUARANTEE];
  const effectiveDateChanged = lastEffectiveDate !== effectiveDateToUse;
  const rateGuaranteeChanged = lastRateGuarantee !== rateGuaranteeValue;

  if (!wasManuallyEdited || effectiveDateChanged || rateGuaranteeChanged) {
    if (!rateGuaranteeValue || rateGuaranteeValue.trim() === "") {
      delete coverageValues[FORM_FIELDS.RENEWAL_DATE];
      delete coverageValues[FORM_FIELDS.LAST_EFFECTIVE_DATE];
      delete coverageValues[FORM_FIELDS.LAST_RATE_GUARANTEE];
    } else {
      const newRenewalDate = calculateNextRenewalDate(
        effectiveDateToUse,
        rateGuaranteeValue,
      );
      if (newRenewalDate) {
        coverageValues[FORM_FIELDS.RENEWAL_DATE] = newRenewalDate;
        coverageValues[FORM_FIELDS.LAST_EFFECTIVE_DATE] = effectiveDateToUse;
        coverageValues[FORM_FIELDS.LAST_RATE_GUARANTEE] = rateGuaranteeValue;
        if (effectiveDateChanged || rateGuaranteeChanged) {
          coverageValues[FORM_FIELDS.RENEWAL_DATE_MANUAL_ENTRY] = false;
        }
      }
    }
  }
};

export const initializeRenewalDates = (
  formMethods: FinalDecisionFormGeneratorProps["formMethods"],
  revisedEffectiveDate: string | null | undefined,
  originalEffectiveDate: string | null | undefined,
): void => {
  const currentCoveragesValues =
    (formMethods.getValues("currentCoverages") as CoverageFormValues[]) || [];
  const newCoveragesValues =
    (formMethods.getValues("newCoverages") as CoverageFormValues[]) || [];
  const effectiveDateToUse = revisedEffectiveDate || originalEffectiveDate;

  const processCoverages = (
    coverages: CoverageFormValues[],
  ): CoverageFormValues[] =>
    coverages.map((coverage) => {
      const rateGuarantee = coverage[FORM_FIELDS.RATE_GUARANTEE] as
        | string
        | undefined;

      updateRenewalDate(coverage, rateGuarantee, effectiveDateToUse);

      return coverage;
    });

  if (currentCoveragesValues.length > 0) {
    formMethods.setValue(
      "currentCoverages",
      processCoverages(currentCoveragesValues),
      {
        shouldValidate: false,
      },
    );
  }
  if (newCoveragesValues.length > 0) {
    formMethods.setValue("newCoverages", processCoverages(newCoveragesValues), {
      shouldValidate: false,
    });
  }
};

export const getFilteredProductOptions = (
  coverage: Coverage,
  productsMap: Map<number, ClientProductCarrierWithCarrier>,
  products: Option[],
): Option[] =>
  products.filter((product) => {
    const productEntry = productsMap.get(product.value as number);
    return productEntry?.productId === coverage.productId;
  });

export const computeGaOverrideAmount = (
  premiumStr: string | null,
  percentageStr: string | null,
): string => {
  if (!premiumStr || !percentageStr) {
    return "";
  }

  const premium = parseFloat(
    String(premiumStr).replace(/[^0-9.-]/g, "") || "0",
  );
  const percentage = parseFloat(
    String(percentageStr).replace(/[^0-9.-]/g, "") || "0",
  );

  if (percentage < 0 || percentage > 99) {
    return "";
  }

  return ((premium * percentage) / 100).toFixed(2);
};

export const isLastPaymentVisible = (
  finalDecision: string,
  incumbentContractedCarrier?: boolean,
): boolean =>
  finalDecision === FinalDecisionStatus.RenewingWithIncumbent &&
  incumbentContractedCarrier === true;

const STALE_PAYMENT_MONTHS = 6;

export const isLastPaymentStale = (
  lastPaymentDate: string | null | undefined,
  today: Date = new Date(),
): boolean => {
  // A missing last payment date means no payment is on record, which the
  // business treats the same as a long-lapsed one: both prefill the winning
  // GA override to 0%.
  if (!lastPaymentDate) {
    return true;
  }
  // Payment dates sync as date-only values at midnight UTC, so compare calendar
  // days to keep the result independent of the time of day and timezone.
  const paymentDay = parseISO(lastPaymentDate.slice(0, 10));
  return isBefore(
    paymentDay,
    subMonths(startOfDay(today), STALE_PAYMENT_MONTHS),
  );
};

export const getRenewalWinningGaPercentage = (
  incumbentPercentage: string | null,
  lastPaymentDate: string | null | undefined,
): string | null =>
  isLastPaymentStale(lastPaymentDate) ? "0" : incumbentPercentage;

export const updateIncumbentProductFields = (
  coverageValues: CoverageFormValues,
  productId: number,
  productMap: Map<number, ClientProductCarrierWithCarrier>,
): void => {
  const product = productMap.get(productId);
  const isContracted = product?.carrier?.isContracted ?? false;
  const carrierId = product?.carrier?.id;
  const annualizedPremium = product?.annualizedPremium;

  const currentManualIncumbentProductId = coverageValues[
    FORM_FIELDS.INCUMBENT_PRODUCT_ID_MANUAL_ENTRY
  ] as number | null;

  coverageValues[FORM_FIELDS.INCUMBENT_PRODUCT_ID] = productId;
  coverageValues[FORM_FIELDS.INCUMBENT_CONTRACTED_CARRIER] = isContracted;
  coverageValues[FORM_FIELDS.INCUMBENT_CARRIER_ID] = carrierId;

  if (!isContracted) {
    coverageValues[FORM_FIELDS.INCUMBENT_GA_OVERRIDE_PERCENTAGE] = null;
    coverageValues[FORM_FIELDS.INCUMBENT_GA_ANNUAL_OVERRIDE_AMOUNT] = null;
  }

  const shouldOverwriteIncumbentAnnualPremium =
    currentManualIncumbentProductId !== productId;

  if (shouldOverwriteIncumbentAnnualPremium) {
    coverageValues[FORM_FIELDS.INCUMBENT_ANNUAL_PREMIUM] =
      annualizedPremium !== null && annualizedPremium !== undefined
        ? annualizedPremium.toString()
        : null;
  }

  // If renewing with incumbent, copy incumbent data to winning fields
  const finalDecision = coverageValues[
    FORM_FIELDS.FINAL_DECISION_STATUS
  ] as FinalDecisionStatus | null;

  if (finalDecision === FinalDecisionStatus.RenewingWithIncumbent) {
    coverageValues[FORM_FIELDS.WINNING_CONTRACTED_CARRIER] = isContracted;
    coverageValues[FORM_FIELDS.WINNING_CARRIER_ID] = carrierId;

    if (isContracted) {
      const winningPercentage = getRenewalWinningGaPercentage(
        coverageValues[FORM_FIELDS.INCUMBENT_GA_OVERRIDE_PERCENTAGE] as
          | string
          | null,
        product?.lastPaymentDate,
      );
      coverageValues[FORM_FIELDS.WINNING_GA_OVERRIDE_PERCENTAGE] =
        winningPercentage;

      const annualPremium = coverageValues[
        FORM_FIELDS.ANNUAL_WRITTEN_PREMIUM
      ] as string | null;

      const overrideAmount = computeGaOverrideAmount(
        annualPremium,
        winningPercentage,
      );
      coverageValues[FORM_FIELDS.WINNING_GA_ANNUAL_OVERRIDE_AMOUNT] =
        overrideAmount;
    } else {
      coverageValues[FORM_FIELDS.WINNING_GA_OVERRIDE_PERCENTAGE] = null;
      coverageValues[FORM_FIELDS.WINNING_GA_ANNUAL_OVERRIDE_AMOUNT] = null;
    }
  }
};

const calculateGaOverrideAmount = (
  fieldName: string,
  currentValue: string,
  premiumField: string,
  percentageField: string,
  amountField: string,
  formMethods: FinalDecisionFormGeneratorProps["formMethods"],
): void => {
  const parentPrefix = getParentField(
    fieldName,
    fieldName
      .split(".")
      .pop() as (typeof FORM_FIELDS)[keyof typeof FORM_FIELDS],
  );
  const coverageValues = formMethods.getValues(parentPrefix);

  const premiumStr = fieldName.endsWith(premiumField)
    ? currentValue
    : (coverageValues[premiumField] as string) || "";
  const percentageStr = fieldName.endsWith(percentageField)
    ? currentValue
    : (coverageValues[percentageField] as string | undefined) || "";

  const overrideAmount = computeGaOverrideAmount(premiumStr, percentageStr);
  coverageValues[amountField] = overrideAmount;
  formMethods.setValue(parentPrefix, coverageValues);
};

export const createHandleIncumbentGaOverrideCalculation =
  (
    formMethods: FinalDecisionFormGeneratorProps["formMethods"],
    productMap: Map<number, ClientProductCarrierWithCarrier>,
  ) =>
  (fieldName: string, currentValue: string): void => {
    calculateGaOverrideAmount(
      fieldName,
      currentValue,
      FORM_FIELDS.INCUMBENT_ANNUAL_PREMIUM,
      FORM_FIELDS.INCUMBENT_GA_OVERRIDE_PERCENTAGE,
      FORM_FIELDS.INCUMBENT_GA_ANNUAL_OVERRIDE_AMOUNT,
      formMethods,
    );

    // If renewing with incumbent, also update winning fields
    const parentPrefix = getParentField(
      fieldName,
      fieldName
        .split(".")
        .pop() as (typeof FORM_FIELDS)[keyof typeof FORM_FIELDS],
    );
    const coverageValues = formMethods.getValues(parentPrefix);
    const finalDecision = coverageValues[
      FORM_FIELDS.FINAL_DECISION_STATUS
    ] as FinalDecisionStatus | null;

    if (finalDecision === FinalDecisionStatus.RenewingWithIncumbent) {
      const incumbentContracted = coverageValues[
        FORM_FIELDS.INCUMBENT_CONTRACTED_CARRIER
      ] as boolean | null;

      // The winning GA override only applies to contracted incumbents. Without
      // this guard, a missing payment date (now treated as stale) would prefill
      // 0% here for non-contracted carriers, past the contracted-carrier gate.
      if (!incumbentContracted) {
        coverageValues[FORM_FIELDS.WINNING_GA_OVERRIDE_PERCENTAGE] = null;
        coverageValues[FORM_FIELDS.WINNING_GA_ANNUAL_OVERRIDE_AMOUNT] = null;
        formMethods.setValue(parentPrefix, coverageValues);
        return;
      }

      const percentageStr = fieldName.endsWith(
        FORM_FIELDS.INCUMBENT_GA_OVERRIDE_PERCENTAGE,
      )
        ? currentValue
        : (coverageValues[FORM_FIELDS.INCUMBENT_GA_OVERRIDE_PERCENTAGE] as
            | string
            | undefined) || "";

      const incumbentProductId = coverageValues[
        FORM_FIELDS.INCUMBENT_PRODUCT_ID
      ] as number | null;
      const winningPercentage = getRenewalWinningGaPercentage(
        percentageStr || null,
        incumbentProductId
          ? productMap.get(incumbentProductId)?.lastPaymentDate
          : null,
      );
      coverageValues[FORM_FIELDS.WINNING_GA_OVERRIDE_PERCENTAGE] =
        winningPercentage;

      // Recalculate winning GA override amount
      const annualPremium = coverageValues[
        FORM_FIELDS.ANNUAL_WRITTEN_PREMIUM
      ] as string | null;
      const overrideAmount = computeGaOverrideAmount(
        annualPremium,
        winningPercentage,
      );
      coverageValues[FORM_FIELDS.WINNING_GA_ANNUAL_OVERRIDE_AMOUNT] =
        overrideAmount || null;

      formMethods.setValue(parentPrefix, coverageValues);
    }
  };

const createHandleWinningGaOverrideCalculation =
  (formMethods: FinalDecisionFormGeneratorProps["formMethods"]) =>
  (fieldName: string, currentValue: string): void => {
    calculateGaOverrideAmount(
      fieldName,
      currentValue,
      FORM_FIELDS.ANNUAL_WRITTEN_PREMIUM,
      FORM_FIELDS.WINNING_GA_OVERRIDE_PERCENTAGE,
      FORM_FIELDS.WINNING_GA_ANNUAL_OVERRIDE_AMOUNT,
      formMethods,
    );
  };

const createHandleRateGuaranteeChange =
  (
    formMethods: FinalDecisionFormGeneratorProps["formMethods"],
    revisedEffectiveDate: string | null | undefined,
    originalEffectiveDate: string | null | undefined,
  ) =>
  (fieldName: string, currentValue: string): void => {
    const parentPrefix = getParentField(fieldName, FORM_FIELDS.RATE_GUARANTEE);
    const coverageValues = formMethods.getValues(
      parentPrefix,
    ) as CoverageFormValues;

    const effectiveDateToUse = revisedEffectiveDate || originalEffectiveDate;

    updateRenewalDate(coverageValues, currentValue, effectiveDateToUse);

    formMethods.setValue(parentPrefix, coverageValues, {
      shouldValidate: false,
      shouldDirty: true,
    });
  };

const createHandleRenewalDateManualEdit =
  (
    formMethods: FinalDecisionFormGeneratorProps["formMethods"],
    revisedEffectiveDate: string | null | undefined,
    originalEffectiveDate: string | null | undefined,
  ) =>
  (fieldName: string): void => {
    const parentPrefix = getParentField(fieldName, FORM_FIELDS.RENEWAL_DATE);
    const coverageValues = formMethods.getValues(
      parentPrefix,
    ) as CoverageFormValues;

    const effectiveDateToUse = revisedEffectiveDate || originalEffectiveDate;
    const rateGuarantee = coverageValues[FORM_FIELDS.RATE_GUARANTEE];
    coverageValues[FORM_FIELDS.RENEWAL_DATE_MANUAL_ENTRY] = true;
    coverageValues[FORM_FIELDS.LAST_EFFECTIVE_DATE] = effectiveDateToUse;
    coverageValues[FORM_FIELDS.LAST_RATE_GUARANTEE] = rateGuarantee;

    formMethods.setValue(parentPrefix, coverageValues);
  };

const createHandleIncumbentAnnualPremiumManualEdit =
  (formMethods: FinalDecisionFormGeneratorProps["formMethods"]) =>
  (fieldName: string, currentValue: string): void => {
    const parentPrefix = getParentField(
      fieldName,
      FORM_FIELDS.INCUMBENT_ANNUAL_PREMIUM,
    );
    const coverageValues = formMethods.getValues(
      parentPrefix,
    ) as CoverageFormValues;
    const manualEntry =
      currentValue !== null && currentValue.trim() !== "" ? true : false;
    const currentProductId = coverageValues[
      FORM_FIELDS.INCUMBENT_PRODUCT_ID
    ] as number | null;

    if (manualEntry && currentProductId) {
      coverageValues[FORM_FIELDS.INCUMBENT_PRODUCT_ID_MANUAL_ENTRY] =
        currentProductId;
    } else {
      coverageValues[FORM_FIELDS.INCUMBENT_PRODUCT_ID_MANUAL_ENTRY] = null;
    }

    formMethods.setValue(parentPrefix, coverageValues);
  };

const createHandleIncumbentProductChange =
  (
    formMethods: FinalDecisionFormGeneratorProps["formMethods"],
    productMap: Map<number, ClientProductCarrierWithCarrier>,
  ) =>
  (fieldName: string, value: unknown): void => {
    const parentPrefix = getParentField(
      fieldName,
      FORM_FIELDS.INCUMBENT_PRODUCT_ID,
    );
    const coverageValues = formMethods.getValues(
      parentPrefix,
    ) as CoverageFormValues;

    if (value && typeof value === "number") {
      updateIncumbentProductFields(coverageValues, value, productMap);
    } else {
      coverageValues[FORM_FIELDS.INCUMBENT_CONTRACTED_CARRIER] = null;
      coverageValues[FORM_FIELDS.INCUMBENT_CARRIER_ID] = null;
      coverageValues[FORM_FIELDS.INCUMBENT_ANNUAL_PREMIUM] = null;
      coverageValues[FORM_FIELDS.INCUMBENT_PRODUCT_ID_MANUAL_ENTRY] = null;
    }

    formMethods.setValue(parentPrefix, coverageValues);
  };

const createHandleWinningCarrierChange =
  (
    formMethods: FinalDecisionFormGeneratorProps["formMethods"],
    carrierMap: Map<number, Carrier>,
  ) =>
  (fieldName: string, value: unknown): void => {
    const parentPrefix = getParentField(
      fieldName,
      FORM_FIELDS.WINNING_CARRIER_ID,
    );
    const coverageValues = formMethods.getValues(parentPrefix);
    if (value && typeof value === "number") {
      const carrier = carrierMap.get(value);
      const isContracted = carrier?.isContracted ?? false;
      coverageValues[FORM_FIELDS.WINNING_CONTRACTED_CARRIER] = isContracted;
      if (!isContracted) {
        coverageValues[FORM_FIELDS.WINNING_GA_OVERRIDE_PERCENTAGE] = null;
        coverageValues[FORM_FIELDS.WINNING_GA_ANNUAL_OVERRIDE_AMOUNT] = null;
      }
    } else {
      coverageValues[FORM_FIELDS.WINNING_CONTRACTED_CARRIER] = null;
    }

    formMethods.setValue(parentPrefix, coverageValues);
  };

const getCurrentCoveragesFields = ({
  coverages,
  products,
  productMap,
  carriers,
  carrierMap,
  formMethods,
  revisedEffectiveDate,
  originalEffectiveDate,
}: {
  coverages: Coverage[];
  products: Option[];
  productMap: Map<number, ClientProductCarrierWithCarrier>;
  carriers: Option[];
  carrierMap: Map<number, Carrier>;
  formMethods: FinalDecisionFormGeneratorProps["formMethods"];
  revisedEffectiveDate?: string | null;
  originalEffectiveDate?: string | null;
}): ArrayStepConfig<InputType>[] => {
  const handleIncumbentGaOverrideCalculation =
    createHandleIncumbentGaOverrideCalculation(formMethods, productMap);
  const handleWinningGaOverrideCalculation =
    createHandleWinningGaOverrideCalculation(formMethods);
  const handleRateGuaranteeChange = createHandleRateGuaranteeChange(
    formMethods,
    revisedEffectiveDate,
    originalEffectiveDate,
  );
  const handleRenewalDateManualEdit = createHandleRenewalDateManualEdit(
    formMethods,
    revisedEffectiveDate,
    originalEffectiveDate,
  );
  const handleIncumbentAnnualPremiumManualEdit =
    createHandleIncumbentAnnualPremiumManualEdit(formMethods);
  const handleIncumbentProductChange = createHandleIncumbentProductChange(
    formMethods,
    productMap,
  );
  const handleWinningCarrierChange = createHandleWinningCarrierChange(
    formMethods,
    carrierMap,
  );

  return coverages.map((coverage: Coverage, index: number) => ({
    label: coverage.name,
    fields: [
      {
        type: "sectionHeader",
        name: `sectionHeader-incumbent`,
        label: "Incumbent",
        grid: { size: 12 },
      },
      {
        type: "autocomplete",
        name: FORM_FIELDS.INCUMBENT_PRODUCT_ID,
        required: true,
        label: "Incumbent Product Record",
        options: getFilteredProductOptions(coverage, productMap, products),
        grid: { size: 7 },
        onChange: (_, value) => {
          const fieldName = `currentCoverages.${index}.${FORM_FIELDS.INCUMBENT_PRODUCT_ID}`;
          handleIncumbentProductChange(fieldName, value);
        },
      },
      {
        name: FORM_FIELDS.INCUMBENT_DATA,
        label: "",
        type: FORM_FIELDS.INCUMBENT_DATA,
        grid: { size: 12 },
        dependencies: {
          visible: isVisibleForIncumbentData(FORM_FIELDS.INCUMBENT_DATA),
        },
      },
      {
        ...MONEY_FIELD,
        name: FORM_FIELDS.INCUMBENT_ANNUAL_PREMIUM,
        label: "Incumbent Annual Premium",
        grid: { size: 7 },
        customProps: { required: true },
        onChange: (e) => {
          handleIncumbentAnnualPremiumManualEdit(e.target.name, e.target.value);
          handleIncumbentGaOverrideCalculation(e.target.name, e.target.value);
        },
      },
      {
        ...PERCENTAGE_FIELD,
        name: FORM_FIELDS.INCUMBENT_GA_OVERRIDE_PERCENTAGE,
        label: "Incumbent GA Override Percentage",
        customProps: { required: true },
        grid: { size: 7 },
        onChange: (e) => {
          handleIncumbentGaOverrideCalculation(e.target.name, e.target.value);
        },
        dependencies: {
          visible: isVisibleForFinalDecision({
            fieldName: FORM_FIELDS.INCUMBENT_GA_OVERRIDE_PERCENTAGE,
            validator: (_, incumbentContractedCarrier) =>
              incumbentContractedCarrier === true,
          }),
        },
      },
      {
        ...MONEY_FIELD,
        name: FORM_FIELDS.INCUMBENT_GA_ANNUAL_OVERRIDE_AMOUNT,
        label: "Incumbent GA Override Annual Amount",
        grid: { size: 7 },
        dependencies: {
          visible: isVisibleForFinalDecision({
            fieldName: FORM_FIELDS.INCUMBENT_GA_ANNUAL_OVERRIDE_AMOUNT,
            validator: (_, incumbentContractedCarrier) =>
              incumbentContractedCarrier === true,
          }),
        },
        disabled: true,
      },
      {
        type: "sectionHeader",
        name: `sectionHeader-finalDecisions`,
        label: "Final Decisions",
        grid: { size: 12 },
        dependencies: {
          visible: (formState) =>
            (formState as FinalDecisionFormValues).wonBOR !== NO &&
            (formState as FinalDecisionFormValues).lostBrokerOfRecord !== true,
        },
      },
      {
        name: FORM_FIELDS.FINAL_DECISION_STATUS,
        label: "Coverage Decision",
        type: "radio",
        required: true,
        row: false,
        onChange: (e, value) => {
          const parentPrefix = getParentField(
            e.target.name,
            FORM_FIELDS.FINAL_DECISION_STATUS,
          );
          // reset dependent fields
          const coverageValues = formMethods.getValues(parentPrefix);
          const isNotMovingCarriers =
            value !== FinalDecisionStatus.MovingCarriers;
          if (isNotMovingCarriers) {
            coverageValues[FORM_FIELDS.WINNING_CARRIER_ID] = null;
            coverageValues[FORM_FIELDS.WINNING_CONTRACTED_CARRIER] = null;
          }
          const isRenewingWithIncumbent =
            value === FinalDecisionStatus.RenewingWithIncumbent;
          const isNotRenewingWithIncumbent = !isRenewingWithIncumbent;
          if (isRenewingWithIncumbent) {
            // Copy incumbent data to winning when renewing with incumbent
            const incumbentContracted = coverageValues[
              FORM_FIELDS.INCUMBENT_CONTRACTED_CARRIER
            ] as boolean | null;
            const incumbentPercentage = coverageValues[
              FORM_FIELDS.INCUMBENT_GA_OVERRIDE_PERCENTAGE
            ] as string | null;
            const incumbentCarrierId = coverageValues[
              FORM_FIELDS.INCUMBENT_CARRIER_ID
            ] as number | null;
            const incumbentProductId = coverageValues[
              FORM_FIELDS.INCUMBENT_PRODUCT_ID
            ] as number | null;
            const winningPercentage = incumbentContracted
              ? getRenewalWinningGaPercentage(
                  incumbentPercentage,
                  incumbentProductId
                    ? productMap.get(incumbentProductId)?.lastPaymentDate
                    : null,
                )
              : incumbentPercentage;
            coverageValues[FORM_FIELDS.WINNING_CONTRACTED_CARRIER] =
              incumbentContracted;
            coverageValues[FORM_FIELDS.WINNING_CARRIER_ID] = incumbentCarrierId;
            coverageValues[FORM_FIELDS.WINNING_GA_OVERRIDE_PERCENTAGE] =
              winningPercentage;
            // Recalculate winning GA override amount if we have both premium and percentage
            const annualPremium = coverageValues[
              FORM_FIELDS.ANNUAL_WRITTEN_PREMIUM
            ] as string | null;
            const overrideAmount = computeGaOverrideAmount(
              annualPremium,
              winningPercentage,
            );
            coverageValues[FORM_FIELDS.WINNING_GA_ANNUAL_OVERRIDE_AMOUNT] =
              overrideAmount;
          } else if (isNotRenewingWithIncumbent) {
            coverageValues[FORM_FIELDS.WINNING_GA_OVERRIDE_PERCENTAGE] = null;
            coverageValues[FORM_FIELDS.WINNING_GA_ANNUAL_OVERRIDE_AMOUNT] =
              null;
          }
          if (isNotMovingCarriers && isNotRenewingWithIncumbent) {
            coverageValues[FORM_FIELDS.ANNUAL_WRITTEN_PREMIUM] = null;
            coverageValues[FORM_FIELDS.RENEWAL_DATE] = null;
            coverageValues[FORM_FIELDS.RENEWAL_DATE_MANUAL_ENTRY] = null;
            coverageValues[FORM_FIELDS.LAST_EFFECTIVE_DATE] = null;
            coverageValues[FORM_FIELDS.RATE_GUARANTEE] = null;
            coverageValues[FORM_FIELDS.TECH_CREDITS] = null;
            // if is null, it's coming from terminating coverage, we want to set tech credits to false by default instead of null
          } else if (coverageValues[FORM_FIELDS.TECH_CREDITS] === null) {
            coverageValues[FORM_FIELDS.TECH_CREDITS] = false;
          }
          formMethods.setValue(parentPrefix, coverageValues);
        },
        options: [
          {
            label:
              FINAL_DECISION_STATUS_LABELS[FinalDecisionStatus.MovingCarriers],
            value: FinalDecisionStatus.MovingCarriers,
          },
          {
            label:
              FINAL_DECISION_STATUS_LABELS[
                FinalDecisionStatus.RenewingWithIncumbent
              ],
            value: FinalDecisionStatus.RenewingWithIncumbent,
          },
          {
            label:
              FINAL_DECISION_STATUS_LABELS[
                FinalDecisionStatus.TerminatingCoverage
              ],
            value: FinalDecisionStatus.TerminatingCoverage,
          },
        ],
        grid: { size: 12 },
        dependencies: {
          visible: isBrokerOfRecord,
        },
      },
      {
        ...WINNING_CONTRACTED_CARRIER_FIELD,
        dependencies: {
          visible: isVisibleForFinalDecision({
            fieldName: FORM_FIELDS.WINNING_CONTRACTED_CARRIER,
            validator: (finalDecision) =>
              finalDecision === FinalDecisionStatus.MovingCarriers,
          }),
        },
      },
      {
        ...WINNING_CARRIER_FIELD,
        options: carriers,
        onChange: (_, value) => {
          const fieldName = `currentCoverages.${index}.${FORM_FIELDS.WINNING_CARRIER_ID}`;
          handleWinningCarrierChange(fieldName, value);
        },
        dependencies: {
          visible: isVisibleForFinalDecision({
            fieldName: FORM_FIELDS.WINNING_CARRIER_ID,
            validator: (finalDecision) =>
              finalDecision === FinalDecisionStatus.MovingCarriers,
          }),
        },
      },
      {
        ...RATE_GUARANTEE_FIELD,
        onChange: (e) => {
          handleRateGuaranteeChange(e.target.name, e.target.value);
        },
        customProps: { required: true },
        dependencies: {
          visible: isVisibleForFinalDecision({
            fieldName: FORM_FIELDS.RATE_GUARANTEE,
            validator: (finalDecision) =>
              finalDecision === FinalDecisionStatus.MovingCarriers ||
              finalDecision === FinalDecisionStatus.RenewingWithIncumbent,
          }),
        },
      },
      {
        ...RENEWAL_DATE_FIELD,
        onChange: (e) => {
          if (e?.target?.name) {
            handleRenewalDateManualEdit(e.target.name);
          }
        },
        dependencies: {
          visible: isVisibleForFinalDecision({
            fieldName: FORM_FIELDS.RENEWAL_DATE,
            validator: (finalDecision) =>
              finalDecision === FinalDecisionStatus.RenewingWithIncumbent ||
              finalDecision === FinalDecisionStatus.MovingCarriers,
          }),
        },
      },
      {
        ...ANNUAL_WRITTEN_PREMIUM_FIELD,
        dependencies: {
          visible: isVisibleForFinalDecision({
            fieldName: FORM_FIELDS.ANNUAL_WRITTEN_PREMIUM,
            validator: (finalDecision) =>
              finalDecision === FinalDecisionStatus.MovingCarriers ||
              finalDecision === FinalDecisionStatus.RenewingWithIncumbent,
          }),
        },
        onChange: (e) => {
          handleWinningGaOverrideCalculation(e.target.name, e.target.value);
        },
        customProps: { required: true },
      },
      {
        name: FORM_FIELDS.LAST_PAYMENT,
        label: "",
        type: FORM_FIELDS.LAST_PAYMENT,
        grid: { size: 7 },
        dependencies: {
          visible: isVisibleForFinalDecision({
            fieldName: FORM_FIELDS.LAST_PAYMENT,
            validator: isLastPaymentVisible,
          }),
        },
      },
      {
        ...WINNING_GA_OVERRIDE_PERCENTAGE_FIELD,
        dependencies: {
          visible: isVisibleForFinalDecision({
            fieldName: FORM_FIELDS.WINNING_GA_OVERRIDE_PERCENTAGE,
            validator: (finalDecision, _, winningContractedCarrier) =>
              (finalDecision === FinalDecisionStatus.RenewingWithIncumbent ||
                FinalDecisionStatus.MovingCarriers) &&
              winningContractedCarrier === true,
          }),
        },
        onChange: (e) => {
          handleWinningGaOverrideCalculation(e.target.name, e.target.value);
        },
      },
      {
        ...WINNING_GA_ANNUAL_OVERRIDE_AMOUNT_FIELD,
        dependencies: {
          visible: isVisibleForFinalDecision({
            fieldName: FORM_FIELDS.WINNING_GA_ANNUAL_OVERRIDE_AMOUNT,
            validator: (finalDecision, _, winningContractedCarrier) =>
              (finalDecision === FinalDecisionStatus.RenewingWithIncumbent ||
                FinalDecisionStatus.MovingCarriers) &&
              winningContractedCarrier === true,
          }),
        },
        disabled: true,
      },
      {
        ...TECH_CREDIT_FIELD,
        dependencies: {
          visible: isVisibleForFinalDecision({
            fieldName: FORM_FIELDS.TECH_CREDITS,
            validator: (finalDecision) =>
              finalDecision === FinalDecisionStatus.MovingCarriers ||
              finalDecision === FinalDecisionStatus.RenewingWithIncumbent,
          }),
        },
      },
      {
        ...NUMBER_OF_PLANS_QUOTED_FIELD,
      },
      {
        ...NOTES_FIELD,
      },
    ],
  }));
};

const getNewCoveragesFields = ({
  coverages,
  carriers,
  carrierMap,
  formMethods,
  revisedEffectiveDate,
  originalEffectiveDate,
}: {
  coverages: Coverage[];
  carriers: Option[];
  carrierMap: Map<number, Carrier>;
  formMethods: FinalDecisionFormGeneratorProps["formMethods"];
  revisedEffectiveDate?: string | null;
  originalEffectiveDate?: string | null;
}): ArrayStepConfig<InputType>[] => {
  const handleWinningGaOverrideCalculation =
    createHandleWinningGaOverrideCalculation(formMethods);
  const handleRateGuaranteeChange = createHandleRateGuaranteeChange(
    formMethods,
    revisedEffectiveDate,
    originalEffectiveDate,
  );
  const handleRenewalDateManualEdit = createHandleRenewalDateManualEdit(
    formMethods,
    revisedEffectiveDate,
    originalEffectiveDate,
  );
  const handleWinningCarrierChange = createHandleWinningCarrierChange(
    formMethods,
    carrierMap,
  );

  return coverages.map((coverage: Coverage, index: number) => ({
    label: coverage.name,
    fields: [
      {
        name: FORM_FIELDS.SOLD,
        label: "Sold",
        type: "checkbox",
        // NOTE: we need defaultValue because the user not checking should be interpreted as false
        defaultValue: false,
        onChange: (e, checked) => {
          const parentPrefix = getParentField(e.target.name, FORM_FIELDS.SOLD);
          const coverageValues = formMethods.getValues(parentPrefix);
          if (!checked) {
            // reset dependent fields
            coverageValues[FORM_FIELDS.WINNING_CARRIER_ID] = null;
            coverageValues[FORM_FIELDS.RENEWAL_DATE] = null;
            coverageValues[FORM_FIELDS.RENEWAL_DATE_MANUAL_ENTRY] = null;
            coverageValues[FORM_FIELDS.ANNUAL_WRITTEN_PREMIUM] = null;
            coverageValues[FORM_FIELDS.RATE_GUARANTEE] = null;
            coverageValues[FORM_FIELDS.TECH_CREDITS] = null;
            coverageValues[FORM_FIELDS.WINNING_GA_OVERRIDE_PERCENTAGE] = null;
            coverageValues[FORM_FIELDS.WINNING_GA_ANNUAL_OVERRIDE_AMOUNT] =
              null;
            coverageValues[FORM_FIELDS.WINNING_CONTRACTED_CARRIER] = null;
          } else {
            coverageValues[FORM_FIELDS.TECH_CREDITS] = false;
          }
          formMethods.setValue(parentPrefix, coverageValues);
        },
        dependencies: {
          visible: (formState) =>
            (formState as FinalDecisionFormValues).wonBOR !== NO &&
            (formState as FinalDecisionFormValues).lostBrokerOfRecord !== true,
        },
      },
      {
        ...WINNING_CONTRACTED_CARRIER_FIELD,
        dependencies: {
          visible: isVisibleForSold({
            fieldName: FORM_FIELDS.WINNING_CONTRACTED_CARRIER,
          }),
        },
      },
      {
        ...WINNING_CARRIER_FIELD,
        options: carriers,
        onChange: (_, value) => {
          const fieldName = `newCoverages.${index}.${FORM_FIELDS.WINNING_CARRIER_ID}`;
          handleWinningCarrierChange(fieldName, value);
        },
        dependencies: {
          visible: isVisibleForSold({
            fieldName: FORM_FIELDS.WINNING_CARRIER_ID,
          }),
        },
      },
      {
        ...RATE_GUARANTEE_FIELD,
        onChange: (e) => {
          handleRateGuaranteeChange(e.target.name, e.target.value);
        },
        dependencies: {
          visible: isVisibleForSold({
            fieldName: FORM_FIELDS.RATE_GUARANTEE,
          }),
        },
        customProps: { required: true },
      },
      {
        ...RENEWAL_DATE_FIELD,
        onChange: (e) => {
          if (e?.target?.name) {
            handleRenewalDateManualEdit(e.target.name);
          }
        },
        dependencies: {
          visible: isVisibleForSold({
            fieldName: FORM_FIELDS.RENEWAL_DATE,
          }),
        },
      },
      {
        ...ANNUAL_WRITTEN_PREMIUM_FIELD,
        dependencies: {
          visible: isVisibleForSold({
            fieldName: FORM_FIELDS.ANNUAL_WRITTEN_PREMIUM,
          }),
        },
        onChange: (e) => {
          handleWinningGaOverrideCalculation(e.target.name, e.target.value);
        },
        customProps: { required: true },
      },
      {
        ...WINNING_GA_OVERRIDE_PERCENTAGE_FIELD,
        dependencies: {
          visible: isVisibleForSold({
            fieldName: FORM_FIELDS.WINNING_GA_OVERRIDE_PERCENTAGE,
            validator: (sold, winningContractedCarrier) =>
              sold && winningContractedCarrier === true,
          }),
        },
        onChange: (e) => {
          handleWinningGaOverrideCalculation(e.target.name, e.target.value);
        },
      },
      {
        ...WINNING_GA_ANNUAL_OVERRIDE_AMOUNT_FIELD,
        dependencies: {
          visible: isVisibleForSold({
            fieldName: FORM_FIELDS.WINNING_GA_ANNUAL_OVERRIDE_AMOUNT,
            validator: (sold, winningContractedCarrier) =>
              sold && winningContractedCarrier === true,
          }),
        },
        disabled: true,
      },
      {
        ...TECH_CREDIT_FIELD,
        dependencies: {
          visible: isVisibleForSold({
            fieldName: FORM_FIELDS.TECH_CREDITS,
          }),
        },
      },
      {
        ...NUMBER_OF_PLANS_QUOTED_FIELD,
      },
      {
        ...NOTES_FIELD,
      },
    ],
  }));
};

interface GetFinalDecisionFormFieldsOptions {
  isProspect: boolean;
  rfpCoverages?: RfpCoveragesResponse;
  products: Option[];
  productMap: Map<number, ClientProductCarrierWithCarrier>;
  carriers: Option[];
  carrierMap: Map<number, Carrier>;
  onNext: () => Promise<boolean>;
  formMethods: FinalDecisionFormGeneratorProps["formMethods"];
  revisedEffectiveDate?: string | null;
  originalEffectiveDate?: string | null;
}

const getFinalDecisionFormFields = ({
  isProspect,
  rfpCoverages,
  products,
  productMap,
  carriers,
  carrierMap,
  onNext,
  formMethods,
  revisedEffectiveDate,
  originalEffectiveDate,
}: GetFinalDecisionFormFieldsOptions): FormElementConfig<InputType>[] => {
  if (!rfpCoverages) {
    return [];
  }

  const currentCoverages = rfpCoverages.currentCoverages ?? [];
  const newCoverages = rfpCoverages.newCoverages ?? [];
  const hasCurrentCoverages = currentCoverages.length > 0;
  const hasNewCoverages = newCoverages.length > 0;
  const fields: FormElementConfig<InputType>[] = [];

  if (isProspect) {
    fields.push(
      {
        name: "wonBOR",
        label: "Did the broker win for the BOR of this prospect?",
        type: "radio",
        onChange: (e, value) => {
          if (value === NO) {
            cleanCoverages(formMethods, true);
          }
        },
        options: [
          { label: "Yes", value: YES },
          { label: "No", value: NO },
        ],
        grid: { size: 12 },
      },
      {
        ...SELECT_OPTION_COVERAGE_FIELD,
        dependencies: {
          visible: (formState) =>
            (formState as FinalDecisionFormValues).wonBOR === YES,
        },
      },
    );
  } else {
    fields.push(SELECT_OPTION_COVERAGE_FIELD, {
      type: "checkbox",
      name: "lostBrokerOfRecord",
      label: "Lost Broker of Record",
      grid: { size: 12 },
      onChange: (e, value) => {
        if (value === true) {
          // Keep incumbent data when lostBrokerOfRecord is selected
          cleanCoverages(formMethods, true);
        }
      },
    });
  }

  if (hasCurrentCoverages) {
    fields.push({
      ...getSectionField("Current Coverages"),
      children: [
        {
          ...getArrayStepperField("currentCoverages"),
          onNext,
          scrollToStepOnChange: true,
          children: getCurrentCoveragesFields({
            coverages: currentCoverages,
            products,
            productMap,
            carriers,
            carrierMap,
            formMethods,
            revisedEffectiveDate,
            originalEffectiveDate,
          }),
        },
      ],
      dependencies: {
        visible: (formState) => formState.wonBOR !== undefined || !isProspect,
      },
    });
  }
  if (hasNewCoverages) {
    fields.push({
      ...getSectionField("New Coverages"),
      children: [
        {
          ...getArrayStepperField("newCoverages"),
          onNext,
          scrollToStepOnChange: true,
          children: getNewCoveragesFields({
            coverages: newCoverages,
            carriers,
            carrierMap,
            formMethods,
            revisedEffectiveDate,
            originalEffectiveDate,
          }),
        },
      ],
      dependencies: {
        visible: (formState) => formState.wonBOR !== undefined || !isProspect,
      },
    });
  }

  return fields;
};

export { getFinalDecisionFormFields };
